<script setup lang="ts">
import { computed, ref } from 'vue'

import AlertComponent from '@/components/AlertComponent.vue'
import HasPermission from '@/components/HasPermission.vue'
import ServiceIcon from '@/components/ServiceIcon.vue'
import useHasPermissionUser from '@/composables/useHasPermissionUser'
import type { CompanyGroup } from '@/interfaces/companyGroup'
import type { User } from '@/modules/users/interfaces/user'
import { Float } from '@headlessui-float/vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { EllipsisVerticalIcon, PencilSquareIcon, TrashIcon } from '@heroicons/vue/20/solid'
import { useQueryClient } from '@tanstack/vue-query'
import DisclaimerModalPolces from './DisclaimerModalPolices.vue'
import DisclaimerModalUsers from './DisclaimerModalUsers.vue'
import useGroup from '../composables/useGroup'

interface Props {
	group: CompanyGroup
	user: User
	companySearch: number
}

const { hasPermissionUser } = useHasPermissionUser()
const props = defineProps<Props>()
const queryClient = useQueryClient()

const openModalUsers = ref(false)
const openModalPolicies = ref(false)
const openAlert = ref(false)

const { deleteGroupAsync } = useGroup({
	immediate: false,
	params: { id: props.group.id },
})

const handleDeleteGroup = async (groupId: number) => {
	await deleteGroupAsync({
		params: { id: groupId },
		config: {
			onSuccessToast: 'Grupo eliminado correctamente',
			onErrorToast: 'Error al eliminar el grupo',
			raiseToast: true,
		},
	})
	openAlert.value = false
	await queryClient.refetchQueries(['companyGroups'])
}

const canViewPolicies = computed(() =>
	hasPermissionUser('view-policies', 'App\\Models\\CompanyGroup')
)

const showMenu = computed(
	() =>
		hasPermissionUser('view-policies', 'App\\Models\\CompanyGroup') ||
		(hasPermissionUser('delete-policies', 'App\\Models\\CompanyGroup') &&
			props.group.name !== 'Default')
)
</script>

<template>
	<!-- Desktop row -->
	<div
		class="hidden sm:grid sm:grid-cols-4 items-center px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors"
	>
		<!-- Name -->
		<RouterLink
			:to="{ name: 'groups.show.id', params: { id: props.group.id } }"
			:class="
				canViewPolicies ?
					'text-[#021F58] hover:underline cursor-pointer'
				:	'text-gray-900 pointer-events-none'
			"
			class="text-sm font-medium truncate block"
		>
			{{ props.group.name }}
		</RouterLink>

		<!-- Services -->
		<div class="flex items-center justify-center">
			<button
				v-if="group.services?.length"
				class="flex items-center gap-0.5"
				@click.prevent="openModalPolicies = !openModalPolicies"
			>
				<ServiceIcon
					v-for="(service, i) in group.services"
					:key="i"
					:service="service.toString()"
				/>
			</button>
			<span
				v-else
				class="text-xs text-gray-300"
				>—</span
			>
		</div>

		<!-- Members -->
		<div class="flex items-center justify-center">
			<button
				:class="
					canViewPolicies ?
						'cursor-pointer hover:border-[#021F58] hover:text-[#021F58]'
					:	'pointer-events-none'
				"
				class="inline-flex items-center justify-center w-7 h-7 rounded-full border border-gray-200 text-xs font-semibold text-gray-600 transition-colors"
				@click.prevent="openModalUsers = !openModalUsers"
			>
				{{ group.userCount }}
			</button>
		</div>

		<!-- Actions -->
		<div class="flex items-center justify-center">
			<Menu
				v-if="showMenu"
				as="div"
				class="relative"
			>
				<Float
					strategy="fixed"
					placement="bottom-end"
					:offset="4"
					enter="transition ease-out duration-100"
					enter-from="transform opacity-0 scale-95"
					enter-to="transform opacity-100 scale-100"
					leave="transition ease-in duration-75"
					leave-from="transform opacity-100 scale-100"
					leave-to="transform opacity-0 scale-95"
				>
					<MenuButton
						class="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none"
					>
						<EllipsisVerticalIcon class="h-4 w-4" />
					</MenuButton>

					<MenuItems
						class="w-44 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-1"
					>
						<HasPermission
							name-permission="view-policies"
							:entity-type="'App\\Models\\CompanyGroup'"
						>
							<MenuItem v-slot="{ active }">
								<RouterLink
									:to="{
										name: 'groups.show.id',
										params: { id: props.group.id },
									}"
									:class="[
										active ? 'bg-gray-50' : '',
										'flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md',
									]"
								>
									<PencilSquareIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
									Editar
								</RouterLink>
							</MenuItem>
						</HasPermission>

						<HasPermission
							name-permission="delete-policies"
							:entity-type="'App\\Models\\CompanyGroup'"
						>
							<MenuItem
								v-if="group.name !== 'Default'"
								v-slot="{ active }"
							>
								<button
									:class="[
										active ? 'bg-red-50' : '',
										'w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 rounded-md transition-colors',
									]"
									@click="openAlert = true"
								>
									<TrashIcon class="h-4 w-4 flex-shrink-0" />
									Eliminar
								</button>
							</MenuItem>
						</HasPermission>
					</MenuItems>
				</Float>
			</Menu>
		</div>
	</div>

	<!-- Mobile card -->
	<div
		class="sm:hidden flex items-center justify-between px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors"
	>
		<div class="min-w-0 flex-1">
			<RouterLink
				:to="{ name: 'groups.show.id', params: { id: props.group.id } }"
				:class="
					canViewPolicies ? 'text-[#021F58]' : 'text-gray-900 pointer-events-none'
				"
				class="text-sm font-medium block truncate"
			>
				{{ props.group.name }}
			</RouterLink>
			<div class="flex items-center gap-2 mt-1">
				<div
					v-if="group.services?.length"
					class="flex items-center gap-0.5"
				>
					<ServiceIcon
						v-for="(s, i) in group.services"
						:key="i"
						:service="s.toString()"
					/>
				</div>
				<span
					v-if="group.services?.length"
					class="text-xs text-gray-200"
					>•</span
				>
				<span class="text-xs text-gray-400"
					>{{ group.userCount }} integrante{{
						group.userCount !== 1 ? 's' : ''
					}}</span
				>
			</div>
		</div>

		<Menu
			v-if="showMenu"
			as="div"
			class="relative ml-2 flex-shrink-0"
		>
			<Float
				strategy="fixed"
				placement="bottom-end"
				:offset="4"
				enter="transition ease-out duration-100"
				enter-from="transform opacity-0 scale-95"
				enter-to="transform opacity-100 scale-100"
				leave="transition ease-in duration-75"
				leave-from="transform opacity-100 scale-100"
				leave-to="transform opacity-0 scale-95"
			>
				<MenuButton
					class="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none"
				>
					<EllipsisVerticalIcon class="h-4 w-4" />
				</MenuButton>

				<MenuItems
					class="w-44 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none p-1"
				>
					<HasPermission
						name-permission="view-policies"
						:entity-type="'App\\Models\\CompanyGroup'"
					>
						<MenuItem v-slot="{ active }">
							<RouterLink
								:to="{
									name: 'groups.show.id',
									params: { id: props.group.id },
								}"
								:class="[
									active ? 'bg-gray-50' : '',
									'flex items-center gap-2 px-3 py-2 text-sm text-gray-700 rounded-md',
								]"
							>
								<PencilSquareIcon class="h-4 w-4 text-gray-400 flex-shrink-0" />
								Editar
							</RouterLink>
						</MenuItem>
					</HasPermission>

					<HasPermission
						name-permission="delete-policies"
						:entity-type="'App\\Models\\CompanyGroup'"
					>
						<MenuItem
							v-if="group.name !== 'Default'"
							v-slot="{ active }"
						>
							<button
								:class="[
									active ? 'bg-red-50' : '',
									'w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 rounded-md transition-colors',
								]"
								@click="openAlert = true"
							>
								<TrashIcon class="h-4 w-4 flex-shrink-0" />
								Eliminar
							</button>
						</MenuItem>
					</HasPermission>
				</MenuItems>
			</Float>
		</Menu>
	</div>

	<!-- Delete confirmation -->
	<AlertComponent
		:model-value="openAlert"
		type="danger"
		:dismiss="true"
		:on-confirm="() => handleDeleteGroup(props.group.id)"
		:on-cancel="() => (openAlert = false)"
	>
		<template #title>
			<h3>Eliminar grupo: ({{ props.group.name }})</h3>
		</template>
		<template #body>
			<p class="font-normal text-sm">
				Al eliminar el grupo se borrara sus políticas de forma permanente y los
				integrante del grupo serán movidos al grupo Default. ¿Estás seguro de
				eliminar el grupo?
			</p>
		</template>
		<template #closeButton>Cancelar</template>
		<template #button>Eliminar</template>
	</AlertComponent>

	<!-- Modals -->
	<HasPermission
		name-permission="view-policies"
		:entity-type="'App\\Models\\CompanyGroup'"
	>
		<DisclaimerModalPolces
			:group-id="props.group.id"
			:group-name="props.group.name"
			:open-modal-policies="openModalPolicies"
			@open-modal-policies="openModalPolicies = !openModalPolicies"
		/>
	</HasPermission>

	<HasPermission
		name-permission="view-policies"
		:entity-type="'App\\Models\\CompanyGroup'"
	>
		<DisclaimerModalUsers
			:group-id="props.group.id"
			:group-name="props.group.name"
			:open-modal-users="openModalUsers"
			@open-modal-users="openModalUsers = !openModalUsers"
		/>
	</HasPermission>
</template>
